import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { FormField } from "@/components/ui/form-field";
import { Switch } from "@/components/ui/switch";
import { DayField } from "../components/DayField";
import type { DayAxis } from "../models/day-axis";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import AutocompleteSelect from "@/components/ui/autocomplete-select";
import { Link, Euro, Plus, Trash, Ticket, Plane, FileText, Clock, Library, AlertTriangle } from "lucide-react";
import { FlightActivity } from "../models/activity.model";
import { getFlightCompanies } from "@/components/utils/flight-companies";
import { getAirports } from "@/components/utils/airports";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import ActivityDetailView from "./ActivityDetailView";
import { LibraryPicker } from "../components/LibraryPicker";
import type { LibraryActivity } from "@/lib/library/models/library-activity.model";

type FlightTab = 'details' | 'flights' | 'documents' | 'library';

interface FlightModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<FlightActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<FlightActivity>;
  itineraryId: string;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  /** Asse dei giorni: sui template ogni tratta si colloca su "Giorno N". */
  axis?: DayAxis | null;
  defaultDate?: string;
  /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
  readOnly?: boolean;
  /** Il cliente ha gia' pagato: costo e "includi nel prezzo" sono consolidati. */
}

const FlightModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate, axis, defaultDate, readOnly = false }: FlightModalProps) => {
  const { t } = useI18n();

  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<FlightActivity>>({
    id: activityId,
    include_price: true,
    title: '',
    link: '',
    address: '',
    travels: [
      {
        flightNumber: '',
        airline: '',
        link: '',
        from: '',
        to: '',
        fromDate: undefined,
        fromTime: '',
        toDate: undefined,
        toTime: ''
      }
    ],
    cost: 0,
    description: '',
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  useEffect(() => {
    if (isOpen && !initialData && defaultDate) {
      setFormData(prev => ({
        ...prev,
        travels: prev.travels?.map((t, i) => i === 0 ? { ...t, fromDate: defaultDate, toDate: defaultDate } : t) || prev.travels
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  // Import da libreria in due tempi: si sceglie, si conferma, poi si torna ai dati.
  const [pendingImport, setPendingImport] = useState<LibraryActivity | null>(null);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<FlightTab>('details');

  useEffect(() => {
    if (!isOpen) {
      setShowValidationBanner(false);
      setErrors({});
      setActiveTab('details');
      setPendingImport(null);
    }
  }, [isOpen]);

  // Create options for AutocompleteSelect components
  const airlineOptions = getFlightCompanies().map(company => ({
    value: company,
    label: company
  }));

  const airportOptions = getAirports().map(airport => ({
    value: airport,
    label: airport
  }));

  const validateForm = (): Record<string, boolean> => {
    const requiredFields = ['cost', 'description'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof FlightActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || (value as number) < 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    if (!formData.travels || formData.travels.length === 0) {
      newErrors.travels = true;
    } else {
      formData.travels.forEach((travel, index) => {
        const travelRequiredFields = ['flightNumber', 'airline', 'from', 'to', 'fromDate', 'fromTime', 'toDate', 'toTime'];
        travelRequiredFields.forEach(field => {
          const value = travel[field as keyof typeof travel];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors[`travel_${index}_${field}`] = true;
          }
        });

        if (travel.from && travel.to && travel.from.trim() === travel.to.trim()) {
          newErrors[`travel_${index}_from`] = true;
          newErrors[`travel_${index}_to`] = true;
        }
      });
    }

    setErrors(newErrors);
    return newErrors;
  };

  const getDateWithOffset = (dateString: string | null | undefined, offset: number) => {
    if (!dateString) return undefined;
    const baseDate = new Date(dateString);
    if (Number.isNaN(baseDate.getTime())) return undefined;
    baseDate.setDate(baseDate.getDate() + offset);
    return baseDate;
  };

  const fromMinDate = getDateWithOffset(itineraryStartDate, -1);
  const fromMaxDate = getDateWithOffset(itineraryEndDate, 1);
  const toMinDate = getDateWithOffset(itineraryStartDate, -1);
  const toMaxDate = getDateWithOffset(itineraryEndDate, 1);

  const addFlight = () => {
    setFormData(prev => ({
      ...prev,
      travels: [
        ...(prev.travels || []),
        {
          flightNumber: '',
          airline: '',
          link: '',
          from: '',
          to: '',
          fromDate: '',
          fromTime: '',
          toDate: '',
          toTime: ''
        }
      ]
    }));
  };

  const removeFlight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.filter((_, i) => i !== index) || []
    }));
  };

  const updateFlight = (index: number, field: keyof FlightActivity['travels'][0], value: string | number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.map((flight, i) => {
        if (i !== index) return flight;

        const updatedFlight = { ...flight, [field]: value };

        // se sto settando fromDate controllo che toDate sia dopo, altrimenti la resetto
        if (field === 'fromDate' && updatedFlight.toDate) {
          const fromDateValue = new Date(value);
          const toDateValue = new Date(updatedFlight.toDate);
          if (!Number.isNaN(fromDateValue.getTime()) && !Number.isNaN(toDateValue.getTime()) && toDateValue < fromDateValue) {
            updatedFlight.toDate = '';
          }
        }

        return updatedFlight;
      }) || []
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (!Object.values(newErrors).some(error => error)) {
      const title = formData.travels[0].from + ' → ' + formData.travels[0].to;

      onSave({
        ...formData,
        title,
        type: 'flight' as const,
        date: formData.travels[0].fromDate
      });
      onClose();
      return;
    }
    setShowValidationBanner(true);
    const detailsFailed = ['cost', 'description'].some(field => newErrors[field]);
    setActiveTab(detailsFailed ? 'details' : 'flights');
  };

  const requiredError = String(t('validation.fieldRequired'));

  // In modifica non si importa: il tab compare solo creando una nuova attività.
  const canImportFromLibrary = !initialData;

  const confirmImport = () => {
      if (!pendingImport) return;
      const imported = pendingImport.toItineraryActivity(defaultDate);
      // L'id resta quello di questa modale: l'import precompila un inserimento nuovo,
      // non porta con sé l'identità dell'attività di libreria.
      setFormData({ ...(imported as Partial<FlightActivity>), id: activityId });
      setPendingImport(null);
      setErrors({});
      setShowValidationBanner(false);
      setActiveTab('details');
  };
  const hasDetailsError = ['cost', 'description'].some(field => errors[field]);
  // I voli hanno un tab loro: i loro errori non devono accendere "Dati".
  const hasFlightsError = Object.keys(errors).some(key => errors[key] && (key === 'travels' || key.startsWith('travel_')));

  if (readOnly) {
    return (
      <ActivityDetailView
        isOpen={isOpen}
        onClose={onClose}
        icon={icon}
        title={t('builder.activities.flight')}
        activity={formData}
      />
    );
  }

  return (
    <ActivityModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      title={t('builder.activities.flight')}
      formId="flight-form"
      onSubmit={handleSubmit}
      showValidationBanner={showValidationBanner}
      activeTab={activeTab}
      onActiveTabChange={(value) => setActiveTab(value as FlightTab)}
      tabs={[
        {
          key: 'details',
          label: t('modals.tabDetails'),
          icon: <Ticket className="h-3.5 w-3.5 shrink-0" />,
          hasError: hasDetailsError,
          content: (
            <div className="space-y-5">
              {/* Prima riga: il volo non ha titolo ne indirizzo, resta il costo. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  id="cost"
                  label={t('modals.price')}
                  icon={<Euro className="h-3 w-3" />}
                  required
                  hint={t('modals.costHint')}
                  error={errors.cost ? requiredError : undefined}
                >
                  <Input
                    id="cost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                    placeholder="0,00"
                    className="h-9 tabular-nums"
                    hasError={errors.cost}
                  />
                </FormField>

                <div className="flex items-start justify-between gap-4 rounded-md bg-gray-50 px-3 py-2.5">
                  <div className="space-y-0.5">
                    <p className="text-xs text-gray-700">{t('modals.includePrice')}</p>
                    <p className="text-[11px] text-gray-500">{t('modals.includePriceHint')}</p>
                  </div>
                  <Switch
                    checked={formData.include_price || false}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                  />
                </div>
              </div>


              <FormField
                label={t('modals.note')}
                required
                error={errors.description ? requiredError : undefined}
              >
                <RichTextEditor
                  value={formData.description || ''}
                  onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                  placeholder={t('modals.shareClientPreferencesFlight')}
                  hasError={errors.description}
                />
              </FormField>
            </div>
          ),
        },
        {
          key: 'flights',
          label: t('modals.tabFlights'),
          icon: <Plane className="h-3.5 w-3.5 shrink-0" />,
          hasError: hasFlightsError,
          content: (
            <div className="space-y-4">
              {/* Una tratta per blocco: il riquadro raggruppa i campi di un volo,
                  non un singolo campo. */}
                {formData.travels?.map((travel, index) => (
                  <div key={index} className="space-y-4 rounded-md border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">
                        {t('modals.flightN', { n: index + 1 })}
                      </span>
                      {formData.travels && formData.travels.length > 1 && index !== 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFlight(index)}
                          className="h-7 gap-1 px-2 text-xs font-normal text-gray-500"
                        >
                          <Trash className="h-3.5 w-3.5" /> {t('modals.deleteFlight')}
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField
                        label={t('modals.flightNumber')}
                        icon={<Ticket className="h-3 w-3" />}
                        required
                        error={errors[`travel_${index}_flightNumber`] ? requiredError : undefined}
                      >
                        <Input
                          value={travel.flightNumber}
                          onChange={(e) => updateFlight(index, 'flightNumber', e.target.value)}
                          placeholder="AZ 1234"
                          className="h-9"
                          hasError={errors[`travel_${index}_flightNumber`]}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.airline')}
                        required
                        error={errors[`travel_${index}_airline`] ? requiredError : undefined}
                      >
                        <AutocompleteSelect
                          options={airlineOptions}
                          value={travel.airline}
                          onValueChange={(value) => updateFlight(index, 'airline', value)}
                          placeholder={t('modals.airline')}
                          searchPlaceholder={t('modals.searchAirlines')}
                          emptyMessage="No airlines found"
                          manual={true}
                          filterMode="includes"
                          hasError={errors[`travel_${index}_airline`]}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.link')}
                        icon={<Link className="h-3 w-3" />}
                        hint={t('modals.addSupportingLink')}
                      >
                        <Input
                          value={travel.link}
                          onChange={(e) => updateFlight(index, 'link', e.target.value)}
                          placeholder="https://..."
                          className="h-9"
                          hasError={errors[`travel_${index}_link`]}
                        />
                      </FormField>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField
                        label={t('modals.departureAirport')}
                        required
                        error={errors[`travel_${index}_from`] ? requiredError : undefined}
                      >
                        <AutocompleteSelect
                          options={airportOptions}
                          value={travel.from}
                          onValueChange={(value) => updateFlight(index, 'from', value)}
                          placeholder={t('modals.departureAirport')}
                          searchPlaceholder={t('modals.searchAirports')}
                          emptyMessage="No airports found"
                          manual={true}
                          filterMode="includes"
                          hasError={errors[`travel_${index}_from`]}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.departureDate')}
                        required
                        error={errors[`travel_${index}_fromDate`] ? requiredError : undefined}
                      >
                        <DayField
                          axis={axis}
                          date={travel.fromDate}
                          dayIndex={travel.fromDayIndex}
                          onDateChange={(date) => updateFlight(index, 'fromDate', date)}
                          onDayIndexChange={(dayIndex) => updateFlight(index, 'fromDayIndex', dayIndex)}
                          placeholder={String(t('modals.datePlaceholder'))}
                          hasError={errors[`travel_${index}_fromDate`]}
                          minDate={fromMinDate || undefined}
                          maxDate={fromMaxDate || undefined}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.time')}
                        icon={<Clock className="h-3 w-3" />}
                        required
                        error={errors[`travel_${index}_fromTime`] ? requiredError : undefined}
                      >
                        <Input
                          type="time"
                          value={travel.fromTime}
                          onChange={(e) => updateFlight(index, 'fromTime', e.target.value)}
                          className="h-9"
                          hasError={errors[`travel_${index}_fromTime`]}
                        />
                      </FormField>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField
                        label={t('modals.arrivalAirport')}
                        required
                        error={errors[`travel_${index}_to`] ? requiredError : undefined}
                      >
                        <AutocompleteSelect
                          options={airportOptions}
                          value={travel.to}
                          onValueChange={(value) => updateFlight(index, 'to', value)}
                          placeholder={t('modals.arrivalAirport')}
                          searchPlaceholder={t('modals.searchAirports')}
                          emptyMessage="No airports found"
                          manual={true}
                          filterMode="includes"
                          hasError={errors[`travel_${index}_to`]}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.arrivalDate')}
                        required
                        error={errors[`travel_${index}_toDate`] ? requiredError : undefined}
                      >
                        <DayField
                          axis={axis}
                          date={travel.toDate}
                          dayIndex={travel.toDayIndex}
                          onDateChange={(date) => updateFlight(index, 'toDate', date)}
                          onDayIndexChange={(dayIndex) => updateFlight(index, 'toDayIndex', dayIndex)}
                          placeholder={String(t('modals.datePlaceholder'))}
                          hasError={errors[`travel_${index}_toDate`]}
                          // L'arrivo non può precedere la partenza; può cadere il giorno dopo.
                          minDate={travel.fromDate || toMinDate || undefined}
                          maxDate={toMaxDate || undefined}
                          minIndex={travel.fromDayIndex || 1}
                          extraDays={1}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.time')}
                        icon={<Clock className="h-3 w-3" />}
                        required
                        error={errors[`travel_${index}_toTime`] ? requiredError : undefined}
                      >
                        <Input
                          type="time"
                          value={travel.toTime}
                          onChange={(e) => updateFlight(index, 'toTime', e.target.value)}
                          className="h-9"
                          hasError={errors[`travel_${index}_toTime`]}
                        />
                      </FormField>
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addFlight}
                  className="h-8 w-full border-dashed text-xs font-normal text-gray-600"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  {t('modals.addFlight')}
                </Button>
              </div>
          ),
        },
        {
          key: 'documents',
          optional: true,
          label: t('modals.tabDocuments'),
          icon: <FileText className="h-3.5 w-3.5 shrink-0" />,
          hasContent: (formData.documentation?.length || 0) > 0,
          content: (
            <FormField label={t('modals.documentation')} hint={t('modals.documentationHint')}>
              <UploadMultiFiles
                files={formData.documentation || []}
                onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                label={t('modals.uploadBoardingPass')}
                maxFiles={5}
                itineraryId={itineraryId}
                activityId={activityId}
              />
            </FormField>
          ),
        },
        {
          key: 'library',
          group: 'import' as const,
          optional: true,
          hidden: !canImportFromLibrary,
          label: t('library.tabImport'),
          icon: <Library className="h-3.5 w-3.5 shrink-0" />,
          content: (
            <div className="space-y-4">
              {pendingImport && (
                <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-center gap-2 text-xs font-medium text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {t('library.importConfirmTitle', { title: pendingImport.title })}
                  </p>
                  <p className="text-[11px] text-amber-700">{t('library.importConfirmBody')}</p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={confirmImport}>
                      {t('library.importConfirmAction')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setPendingImport(null)}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              )}

              <LibraryPicker
                activityType="flight"
                enabled={isOpen && activeTab === 'library'}
                onSelect={setPendingImport}
                listClassName="max-h-[40vh]"
              />
            </div>
          ),
        },
      ]}
    />
  );
};

export default FlightModal;
